/**
 * Mercado API module — módulo "Mercado" (datos del scraper de mercado).
 *
 * El backend guarda el historial scrapeado (`scrap_historial`) + el catálogo
 * de códigos (`scrap_codigos`) y expone endpoints admin (estado, mapeo,
 * análisis por combinación de fechas, configuración) y un resumen
 * simplificado para clientes. Ver docs/modulo-mercado.md.
 *
 * Todas las funciones delegan en `apiFetch` (auth, JSON, toasts).
 */

import { apiFetch } from "./apiClient";

// ─── Estado (tab "Estado y datos") ───────────────────────────────────────────

/** Fila del catálogo de códigos del scraper con sus agregados. */
export interface MercadoCodigo {
  codigo: string;
  activa: boolean;
  /** Última vez que el scraper reportó este código (ISO). */
  ultima_vez: string | null;
  /**
   * IDs cortos de Stays que este código representa, según el scraper.
   * Un código puede cubrir VARIAS propiedades (deptos similares del mismo
   * edificio/zona); el backend auto-vincula por id_propiedad_corto.
   */
  staysIds: string[];
  /** Cantidad de filas de historial para este código. */
  filas: number;
  /** Última fecha de consulta scrapeada (ISO YYYY-MM-DD). */
  ultimaFechaConsulta: string | null;
  /**
   * Propiedades cuyo ID Stays está en este código pero que el admin DESVINCULÓ
   * a mano: la ingesta ya no las vuelve a vincular solas. Round 2026-09-11.
   */
  autovinculoBloqueado?: { id: number; nombre: string }[];
  /** Propiedades del ERP vinculadas a este código (vacío = sin mapear). */
  propiedades: { id: number; nombre: string; visibleCliente: boolean }[];
  /**
   * Costo de aseo del análisis: promedio del precio de limpieza total
   * (`precio_limpieza_cobrado`, ERP) de las propiedades mapeadas al código;
   * `null` si ninguna tiene precio (el análisis usa entonces $40.000 default).
   * REGLA: el costo de aseo del scraper NUNCA se usa (el backend ya no lo
   * expone) — el análisis de mercado usa siempre el precio de limpieza del ERP.
   */
  costoLimpiezaSugerido: number | null;
}

export interface MercadoEstado {
  totalFilas: number;
  totalCodigos: number;
  sinMapear: number;
  /** Timestamp ISO de la última ingesta (null = nunca). */
  ultimaIngesta: string | null;
  codigos: MercadoCodigo[];
}

// ─── Rangos y serie (tab "Análisis") ─────────────────────────────────────────

/** Combinación (checkin, checkout) disponible para un código. */
export interface MercadoRango {
  checkin: string;
  checkout: string;
  registros: number;
  ultimaConsulta: string | null;
  /** true si el checkin es futuro (aún se puede actuar sobre el precio). */
  esFutura: boolean;
}

/**
 * Punto de la serie histórica (valores STAYS corregidos por aseo, CLP).
 * `cuantoFalta` está en la convención de SCRAP: NEGATIVO (= fecha_consulta −
 * checkin; 0 = día del check-in). El eje X va de negativo → 0 (izq → der).
 */
export interface MercadoSeriePunto {
  cuantoFalta: number;
  disponibles: number;
  min: number | null;
  median: number | null;
  max: number | null;
  d1: number | null;
  d2: number | null;
  d3: number | null;
  d4: number | null;
  d5: number | null;
  d6: number | null;
  d7: number | null;
  d8: number | null;
  d9: number | null;
}

export type MercadoConfianza = "sin_datos" | "baja" | "media" | "alta";

/** Tendencia de la DISPONIBILIDAD hacia el check-in (lógica SCRAP). */
export type MercadoTendenciaDisponibilidad = "subiendo" | "bajando" | "plana";

export interface MercadoRegresion {
  /** Pendiente (disponibles por día, convención SCRAP: x = −cuantoFalta_erp). */
  a: number;
  /** Intercepto: disponibles proyectados el día del checkin (x=0). */
  b: number;
  /** Día (cuantoFalta) en que la oferta llegaría a 0, si a ≠ 0. */
  xZero: number | null;
  valida: boolean;
  r2: number | null;
  confianza: MercadoConfianza;
  /** Tendencia de disponibilidad; null si la regresión no es válida. */
  tendencia: MercadoTendenciaDisponibilidad | null;
  /** Razón cuando la regresión no corre (null si válida). */
  razon: string | null;
}

export interface MercadoCotas {
  /** Labels de decil ("min"/"D1".."D9"/"max"); null si sin regresión. */
  minLabel: string | null;
  maxLabel: string | null;
  minValue: number | null;
  maxValue: number | null;
}

export interface MercadoSuficiencia {
  puntos: number;
  umbralMinimo: number;
  suficiente: boolean;
  razon: string | null;
}

export interface MercadoSerie {
  noches: number;
  correccionPorNoche: number;
  /** Aseo efectivamente usado (override del form o sugerido de la propiedad). */
  costoLimpieza: number;
  serie: MercadoSeriePunto[];
  regresion: MercadoRegresion;
  cotas: MercadoCotas;
  suficiencia: MercadoSuficiencia;
  mensaje: string;
}

// ─── Configuración (singleton mercado_settings) ──────────────────────────────

export type MercadoDemandaMetodo = "percentil" | "absoluto";

export interface MercadoSettings {
  habilitadoClientes: boolean;
  ventanaDias: number;
  minPuntosCliente: number;
  redondeo: number;
  decilInferior: number;
  decilSuperior: number;
  aplicarCorreccionAseo: boolean;
  demandaMetodo: MercadoDemandaMetodo;
  demandaPctAlta: number;
  demandaPctBaja: number;
  demandaAbsAlta: number;
  demandaAbsBaja: number;
  tendenciaUmbralPct: number;
  /** Id de la paleta de colores de la demanda (utils/mercadoPaletas.ts). */
  paletaDemanda: string;
}

/** Mismo shape `{ data, defaults }` que general-settings. */
export interface MercadoSettingsResponse {
  data: MercadoSettings;
  defaults: MercadoSettings;
}

// ─── Resumen cliente (vista simplificada) ────────────────────────────────────

/** Demanda en 5 niveles; el color sale de la paleta activa (utils/mercadoPaletas.ts). */
export type MercadoDemanda = "muy_alta" | "alta" | "media" | "baja" | "muy_baja";

/**
 * Una fecha (combo checkin/checkout) en la vista CLIENTE. Round 2026-07-23: el
 * cliente ve SOLO la ocupación estimada del mercado (0..100, proyectada al
 * check-in) y su nivel de demanda — NUNCA precios (se quitaron `banda` y la
 * `tendencia` de precio del payload; viven solo en el análisis admin).
 */
export interface MercadoFechaCliente {
  checkin: string;
  checkout: string;
  noches: number;
  demanda: MercadoDemanda;
  /** Ocupación estimada del mercado 0..100 al check-in; null si sin regresión. */
  ocupacion: number | null;
  mensaje: string;
  confianza: MercadoConfianza;
}

/** Aviso/nota informativa que se muestra en el calendario (shape público). */
export interface MercadoNota {
  id: number;
  titulo: string;
  mensaje: string;
  fechaInicio: string; // YYYY-MM-DD
  fechaFin: string; // YYYY-MM-DD
}

/** Aviso en la vista admin (CRUD): incluye alcance + propiedades + activo. */
export interface MercadoNotaAdmin extends MercadoNota {
  alcance: "global" | "propiedades";
  propiedadIds: number[];
  activo: boolean;
}

export interface MercadoPropiedadCliente {
  id: number;
  nombre: string;
  precioMinimoNoche: number | null;
  fechas: MercadoFechaCliente[];
}

export interface ResumenClienteResponse {
  success: boolean;
  habilitado: boolean;
  /** Id de la paleta de colores de la demanda (utils/mercadoPaletas.ts). */
  paletaId?: string;
  propiedades: MercadoPropiedadCliente[];
}

/** Respuesta de la vista CALENDARIO de una propiedad (rango libre, pasado+futuro). */
export interface MercadoCalendarioResponse {
  success: boolean;
  habilitado: boolean;
  /** null si el kill-switch está apagado, la propiedad no es del cliente/visible, o sin código. */
  propiedad: { id: number; nombre: string; precioMinimoNoche: number | null } | null;
  /** Id de la paleta de colores de la demanda (utils/mercadoPaletas.ts). */
  paletaId?: string;
  /** Combos del rango, ubicados por `checkin` en el calendario. */
  dias: MercadoFechaCliente[];
  /** Avisos informativos activos que se solapan con el rango. */
  notas?: MercadoNota[];
}

// ─── Calendario del análisis (admin, SIN censura) ────────────────────────────

/** Un día del calendario del análisis admin: ocupación EXACTA + banda de precio. */
export interface MercadoAnalisisDia {
  checkin: string;
  checkout: string;
  noches: number;
  /** Ocupación estimada 0..100 (sin redondear); null si sin regresión. */
  ocupacion: number | null;
  demanda: MercadoDemanda;
  /** Banda de precio sugerida (neta de aseo, redondeada a `redondeo`). */
  banda: { desde: number; hasta: number } | null;
  /** Diagnóstico crudo de SCRAP (admin ve todo; incluye deciles/precios). */
  mensaje?: string;
  confianza: MercadoConfianza;
}

export interface MercadoAnalisisCalendarioResponse {
  success: boolean;
  codigo: string;
  /** Id de la paleta de colores (misma que ve el cliente). */
  paletaId?: string;
  /** Aseo usado (override del form o sugerido de la propiedad). */
  costoLimpieza: number;
  dias: MercadoAnalisisDia[];
  /** Avisos informativos activos que se solapan con el rango. */
  notas?: MercadoNota[];
}

/** Un combo scrapeado (estadía checkin→checkout que el SCRAP midió) con SU
 * ocupación y SU banda de precio. El valor pertenece al rango scrapeado. */
export interface MercadoResumenCombo {
  checkin: string;
  checkout: string;
  noches: number;
  /** Ocupación estimada 0..100 (exacta) de esta estadía. */
  ocupacion: number;
  /** Banda de precio sugerida (redondeada); null si no se pudo estimar. */
  banda: { desde: number; hasta: number } | null;
}

/**
 * Datos de mercado por PROPIEDAD para la fila informativa del calendario admin:
 * la lista de combos scrapeados que solapan el rango. El frontend pinta cada
 * combo sobre SUS días (no promedia el rango visible).
 */
export interface MercadoCalendarioResumenItem {
  propiedadId: number;
  /** false = sin código de mercado o sin combos en el rango. */
  tieneDatos: boolean;
  combos: MercadoResumenCombo[];
}

export interface MercadoCalendarioResumenResponse {
  success: boolean;
  desde: string;
  hasta: string;
  propiedades: MercadoCalendarioResumenItem[];
}

// ─── Borrado total ───────────────────────────────────────────────────────────

/** Summary del borrado total (patrón `{ eliminados }` de borrados en cascada). */
export interface MercadoBorradoEliminados {
  scrap_historial: number;
  scrap_codigos: number;
  propiedades_desvinculadas: number;
}

// ─── mercadoApi ──────────────────────────────────────────────────────────────

const BASE = "/mercado";

export const mercadoApi = {
  getSecreto: (): Promise<{ success: boolean; configurado: boolean; panelDisponible: boolean; origen: "panel" | "servidor" | null } | null> =>
    apiFetch(`${BASE}/secreto`, { errorMsg: "Error al consultar la conexión del scraper" }),
  guardarSecreto: (secret: string): Promise<{ success: boolean } | null> =>
    apiFetch(`${BASE}/secreto`, { method: "PUT", body: JSON.stringify({ secret }), successMsg: "Clave del scraper guardada", errorMsg: "Error al guardar la clave del scraper" }),
  /** GET /mercado/estado — KPIs + catálogo de códigos con agregados. */
  getEstado: (): Promise<{ success: boolean; data: MercadoEstado } | null> =>
    apiFetch<{ success: boolean; data: MercadoEstado }>(`${BASE}/estado`, {
      errorMsg: "Error al cargar el estado del mercado",
    }),

  /**
   * PUT /mercado/codigos/:codigo/mapeo — fija el conjunto EXACTO de
   * propiedades vinculadas al código (N propiedades por código; lista vacía
   * desvincula todas).
   */
  setMapeo: (
    codigo: string,
    propiedadIds: number[],
    /**
     * Lo que el admin estaba viendo vinculado al editar. El backend solo
     * desvincula lo que se quitó de ESTA lista: una propiedad vinculada mientras
     * tanto (por un envío del scraper u otro admin) se conserva. Round 2026-09-11.
     */
    propiedadIdsAntes?: number[],
  ): Promise<{ success: boolean } | null> =>
    apiFetch<{ success: boolean }>(
      `${BASE}/codigos/${encodeURIComponent(codigo)}/mapeo`,
      {
        method: "PUT",
        body: JSON.stringify({ propiedadIds, propiedadIdsAntes }),
        successMsg: "Vinculación actualizada",
        errorMsg: "Error al actualizar la vinculación",
      },
    ),

  /**
   * PUT /mercado/visibilidad-cliente — fija en masa qué propiedades muestran
   * su resumen de mercado al cliente dueño (capa granular bajo el kill-switch
   * global `habilitadoClientes`). `visible=false` las oculta.
   */
  setVisibilidadCliente: (
    propiedadIds: number[],
    visible: boolean,
  ): Promise<{ success: boolean; afectadas: number } | null> =>
    apiFetch<{ success: boolean; afectadas: number }>(`${BASE}/visibilidad-cliente`, {
      method: "PUT",
      body: JSON.stringify({ propiedadIds, visible }),
      successMsg: visible
        ? "Propiedad(es) visibles para el cliente"
        : "Propiedad(es) ocultas para el cliente",
      errorMsg: "Error al actualizar la visibilidad",
    }),

  /** GET /mercado/rangos?codigo= — combos (checkin, checkout) del código. */
  getRangos: (
    codigo: string,
  ): Promise<{ success: boolean; data: MercadoRango[] } | null> =>
    apiFetch<{ success: boolean; data: MercadoRango[] }>(
      `${BASE}/rangos?codigo=${encodeURIComponent(codigo)}`,
      { errorMsg: "Error al cargar las fechas del código" },
    ),

  /**
   * GET /mercado/serie — análisis completo de una combinación de fechas.
   * `costoLimpieza` (override del aseo) y `minimo` (ventana de puntos recientes,
   * default 20) son opcionales; se omiten de la URL cuando no se pasan.
   */
  getSerie: (
    codigo: string,
    checkin: string,
    checkout: string,
    costoLimpieza?: number,
    minimo?: number,
  ): Promise<{ success: boolean; data: MercadoSerie } | null> => {
    let url =
      `${BASE}/serie?codigo=${encodeURIComponent(codigo)}` +
      `&checkin=${encodeURIComponent(checkin)}&checkout=${encodeURIComponent(checkout)}`;
    if (costoLimpieza != null && Number.isFinite(costoLimpieza)) {
      url += `&costoLimpieza=${Math.round(costoLimpieza)}`;
    }
    if (minimo != null && Number.isFinite(minimo)) {
      url += `&minimo=${Math.round(minimo)}`;
    }
    return apiFetch<{ success: boolean; data: MercadoSerie }>(url, {
      errorMsg: "Error al cargar el análisis",
    });
  },

  /**
   * GET /mercado/analisis/calendario — vista CALENDARIO del análisis admin de un
   * código en un rango libre [desde, hasta]. Ocupación exacta + banda de precio
   * (sin censura). `costoLimpieza` (override) y `minimo` son opcionales.
   */
  getAnalisisCalendario: (
    codigo: string,
    desde: string,
    hasta: string,
    costoLimpieza?: number,
    minimo?: number,
  ): Promise<MercadoAnalisisCalendarioResponse | null> => {
    let url =
      `${BASE}/analisis/calendario?codigo=${encodeURIComponent(codigo)}` +
      `&desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`;
    if (costoLimpieza != null && Number.isFinite(costoLimpieza)) {
      url += `&costoLimpieza=${Math.round(costoLimpieza)}`;
    }
    if (minimo != null && Number.isFinite(minimo)) {
      url += `&minimo=${Math.round(minimo)}`;
    }
    return apiFetch<MercadoAnalisisCalendarioResponse>(url, {
      errorMsg: "Error al cargar el calendario del análisis",
    });
  },

  /**
   * GET /mercado/calendario-resumen — resumen de mercado por RANGO para la fila
   * informativa del calendario admin: una ocupación + una banda por propiedad.
   * `propiedadIds` (opcional) acota a las propiedades visibles; sin él, todas las
   * que tengan código de mercado.
   */
  getCalendarioResumen: (
    desde: string,
    hasta: string,
    propiedadIds?: number[],
  ): Promise<MercadoCalendarioResumenResponse | null> => {
    let url =
      `${BASE}/calendario-resumen?desde=${encodeURIComponent(desde)}` +
      `&hasta=${encodeURIComponent(hasta)}`;
    if (propiedadIds && propiedadIds.length) {
      url += `&propiedadIds=${propiedadIds.join(",")}`;
    }
    return apiFetch<MercadoCalendarioResumenResponse>(url, {
      // Silencioso: es una capa informativa opcional; un fallo no debe molestar.
      silent: true,
    });
  },

  /** GET /mercado/config — configuración del módulo (`{ data, defaults }`). */
  getConfig: (): Promise<MercadoSettingsResponse | null> =>
    apiFetch<MercadoSettingsResponse>(`${BASE}/config`, {
      errorMsg: "Error al cargar la configuración del mercado",
    }),

  /** PUT /mercado/config — guardar configuración (parcial). */
  updateConfig: (
    body: Partial<MercadoSettings>,
  ): Promise<MercadoSettingsResponse | null> =>
    apiFetch<MercadoSettingsResponse>(`${BASE}/config`, {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Configuración del mercado guardada",
      errorMsg: "Error al guardar la configuración del mercado",
    }),

  /**
   * DELETE /mercado/datos — borra TODO lo del scraper en el ERP (historial +
   * catálogo de códigos + desvinculación de propiedades). NO toca la
   * configuración del módulo. Recuperable re-enviando desde el SCRAP 2.0.
   */
  borrarDatos: (): Promise<{
    success: boolean;
    eliminados: MercadoBorradoEliminados;
  } | null> =>
    apiFetch<{ success: boolean; eliminados: MercadoBorradoEliminados }>(
      `${BASE}/datos`,
      {
        method: "DELETE",
        errorMsg: "Error al borrar los datos del scraper",
      },
    ),

  // ── Avisos / notas informativas (admin) ────────────────────────────────────
  /** GET /mercado/notas — todos los avisos (admin). */
  getNotas: (): Promise<{ success: boolean; data: MercadoNotaAdmin[] } | null> =>
    apiFetch<{ success: boolean; data: MercadoNotaAdmin[] }>(`${BASE}/notas`, {
      errorMsg: "Error al cargar los avisos",
    }),

  /** POST /mercado/notas — crea un aviso. */
  createNota: (
    body: Omit<MercadoNotaAdmin, "id">,
  ): Promise<{ success: boolean; data: MercadoNotaAdmin } | null> =>
    apiFetch<{ success: boolean; data: MercadoNotaAdmin }>(`${BASE}/notas`, {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Aviso creado",
      errorMsg: "Error al crear el aviso",
    }),

  /** PUT /mercado/notas/:id — edita un aviso. */
  updateNota: (
    id: number,
    body: Omit<MercadoNotaAdmin, "id">,
  ): Promise<{ success: boolean; data: MercadoNotaAdmin } | null> =>
    apiFetch<{ success: boolean; data: MercadoNotaAdmin }>(`${BASE}/notas/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Aviso actualizado",
      errorMsg: "Error al actualizar el aviso",
    }),

  /** DELETE /mercado/notas/:id — borra un aviso. */
  deleteNota: (id: number): Promise<{ success: boolean } | null> =>
    apiFetch<{ success: boolean }>(`${BASE}/notas/${id}`, {
      method: "DELETE",
      successMsg: "Aviso eliminado",
      errorMsg: "Error al eliminar el aviso",
    }),

  /** GET /mercado/cliente/resumen — resumen simplificado (rol cliente). */
  getResumenCliente: (): Promise<ResumenClienteResponse | null> =>
    apiFetch<ResumenClienteResponse>(`${BASE}/cliente/resumen`, {
      errorMsg: "Error al cargar los datos de mercado",
    }),

  /**
   * GET /mercado/cliente/calendario — vista calendario de demanda de una
   * propiedad en un rango libre [desde, hasta] (incluye fechas pasadas). Sirve
   * tanto al cliente (su propiedad visible) como al admin (preview de cualquiera).
   */
  getCalendario: (
    propiedadId: number,
    desde: string,
    hasta: string,
  ): Promise<MercadoCalendarioResponse | null> =>
    apiFetch<MercadoCalendarioResponse>(
      `${BASE}/cliente/calendario?propiedadId=${propiedadId}&desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`,
      { errorMsg: "Error al cargar el calendario de mercado" },
    ),
};
